'use client'

import type { Route } from 'next'
import { useCallback, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { isTeamMemberDisabledError } from '@kiao-voice/api-client'
import { reportHandledException } from '@/lib/error-reporting'
import { navigateWithinSite } from '@/lib/analytics/script-boundary'
import {
  AudioBatchApiError,
  isRecoverableActiveContextError,
  probeInternalAccess,
} from '@/features/batch-transcription/api-client'
import { readStoredToken } from '@/features/batch-transcription/auth-storage'

interface BatchTranscriptionPageProps {
  /** Test seam — defaults to the real `localStorage` read. */
  loadStoredToken?: () => string | null
  /** Test seam — validates a stored token before redirecting (defaults to the
   *  real `/audio-batches/me` probe). A stale/revoked token must NOT count as a
   *  signed-in member. */
  probeSession?: typeof probeInternalAccess
  /** Test seam — defaults to a real Next.js router replace. */
  navigate?: (path: string) => void
  /** Test seam — defaults to the real `window.location.search`. */
  currentSearch?: () => string
}

const readCurrentSearch = () => window.location.search

/**
 * The trial entry: renders nothing and forwards. Members go to their
 * transcripts list (uploading is a modal there); everyone else goes to the free
 * trial on /transcription. Links whose audience may be signed in (the pricing
 * free plan, the anonymous result, public note shares, blog articles) point
 * here rather than at /transcription#try, and old links and bookmarks still
 * arrive here too.
 */
export function BatchTranscriptionPage({
  loadStoredToken = readStoredToken,
  probeSession = probeInternalAccess,
  navigate,
  currentSearch = readCurrentSearch,
}: BatchTranscriptionPageProps) {
  const router = useRouter()
  // One-shot guard so the redirect fires exactly once (React Strict Mode mounts
  // effects twice in dev, which would otherwise call replace() twice).
  const redirectedRef = useRef(false)

  // next/navigation's useRouter returns a fresh object each render in the jsdom
  // mock; hold navigate/router behind refs so the mount effect runs exactly once.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const routerRef = useRef(router)
  routerRef.current = router

  const navigateTo = useCallback(<T extends string>(path: Route<T>) => {
    if (redirectedRef.current) return
    redirectedRef.current = true
    if (navigateRef.current) navigateRef.current(path)
    // Into `/app` this page's Clarity must not follow: see script-boundary.
    else navigateWithinSite(path, () => routerRef.current.replace(path), { replace: true })
  }, [])

  useEffect(() => {
    // Old ad, email and KOL links carry utm / fbclid / ref in the query. The
    // redirect runs before the GA and Pixel snippets load, so dropping it would
    // lose the campaign the visit came from.
    const toTrial = () => navigateTo(`/transcription${currentSearch()}#try` as Route)
    const token = loadStoredToken()
    if (!token) {
      toTrial()
      return
    }
    let cancelled = false
    // Validate the token before redirecting: a stale/revoked token in
    // localStorage is NOT a signed-in member, so on a genuine 401/403 treat the
    // visitor as a guest instead of bouncing them to sign-in. A valid token (or
    // a self-healing stale workspace context) IS a member; /app/transcriptions
    // owns its own auth/error handling for anything transient.
    void probeSession()
      .then(() => {
        if (!cancelled) navigateTo('/app/transcriptions')
      })
      .catch((cause) => {
        reportHandledException(cause, {
          source: 'app/(public)/batch-transcription/batch-transcription-page.tsx',
        })
        if (cancelled) return
        const deadToken =
          cause instanceof AudioBatchApiError &&
          (cause.status === 401 || cause.status === 403) &&
          !isRecoverableActiveContextError(cause) &&
          !isTeamMemberDisabledError(cause)
        if (deadToken) toTrial()
        else navigateTo('/app/transcriptions')
      })
    return () => {
      cancelled = true
    }
  }, [loadStoredToken, probeSession, navigateTo, currentSearch])

  return null
}
